import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardHeader,
  Grid,
  Divider,
  Breadcrumbs,
  Typography,
  Link,
} from '@material-ui/core';

import { Link as RouterLink } from 'react-router-dom';
import CloseIcon from '@material-ui/icons/Close';
import FilterListIcon from '@material-ui/icons/FilterList';
import AddCircleOutlineOutlinedIcon from '@material-ui/icons/AddCircleOutlineOutlined';
import {
  FilterChip,
  QuickSearch,
  useAPI,
  DeleteModal,
  VerticalBoxContainer,
  PageContentWrapper,
} from '@shared/components';
import PageHeader from '../../components/PageHeader';
import ExcelExportButton from '../../components/ExcelExportButton';
import { handleChipDelete } from '../../utils/filterHelpers';
import useTheme from '../../hooks/useTheme';
import axios from '../../utils/axios';
import Table from './Table';
import Filter from './Filter';
import Modal from './Modal';
import useGlobalAlertContext from '../../hooks/useGlobalAlertContext';

const fields = [
  {
    label: 'Redni broj',
    value: 'ordNumber',
  },
  {
    label: 'Šifra',
    value: 'code',
  },
  {
    label: 'Naziv',
    value: 'name',
  },
  {
    label: 'Opis',
    value: 'description',
  },
];

const getLabelText = (field) => {
  const map = {
    name: 'Naziv',
    code: 'Šifra',
    ordNumber: 'Redni broj',
    description: 'Opis',
  };
  return map[field];
};

const getChipValue = (chip, filters) => {
  return filters[chip];
};

const AttributeGroups = ({ history }) => {
  const theme = useTheme();
  const [selectedAttributeGroup, setSelectedAttributeGroup] = useState(null);
  const [filters, setFilters] = useState({});
  const [sorters, setSorters] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [triggerRerender, setRerender] = useState(false);

  const [modalVisible, setModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const { sendSuccessAlertData, sendErrorAlertData } = useGlobalAlertContext();

  const [attributeGroups = { nodes: [], totalCount: 0 }, error, loading] = useAPI(
    () =>
      axios
        .get(`group-attributes`, {
          params: {
            limit: rowsPerPage,
            offset: page * rowsPerPage,
            conditions: filters,
            orderBy: [...sorters, '"ordNumber" ASC'],
          },
        })
        .then(({ data }) => data),
    [page, rowsPerPage, filters, sorters, triggerRerender],
  );

  const handleFilterChange = (filterValues) => {
    setPage(0);
    setFilters({ ...filterValues });
  };

  return (
    <VerticalBoxContainer>
      <Filter
        currentValues={filters}
        visible={filterModalVisible}
        setVisible={setFilterModalVisible}
        handleFilterChange={handleFilterChange}
        getLabelText={getLabelText}
        getChipValue={getChipValue}
        setFilters={setFilters}
        setPage={setPage}
      />
      <Modal
        attributeGroup={selectedAttributeGroup}
        setAttributeGroup={setSelectedAttributeGroup}
        visible={modalVisible}
        setVisible={setModalVisible}
        setRerender={setRerender}
        sendSuccessAlertData={sendSuccessAlertData}
        sendErrorAlertData={sendErrorAlertData}
        resetAttributeGroup={() => setSelectedAttributeGroup(undefined)}
      />
      <DeleteModal
        buttonText="Obriši grupu atributa"
        dataLength={attributeGroups.totalCount}
        message="Uspješno obrisana grupa atributa"
        page={page}
        setPage={setPage}
        objectId={selectedAttributeGroup?.id}
        setObject={setSelectedAttributeGroup}
        path="group-attributes"
        promptText={`Da li ste sigurni da želite obrisati grupu atributa naziva ${selectedAttributeGroup?.name}?`}
        resetObject={() => setSelectedAttributeGroup(undefined)}
        sendSuccessAlertData={sendSuccessAlertData}
        sendErrorAlertData={sendErrorAlertData}
        setRerender={setRerender}
        visible={deleteModalVisible}
        setVisible={setDeleteModalVisible}
        title={`Obriši grupu atributa sa šifrom ${selectedAttributeGroup?.code}`}
        theme={theme}
        axios={axios}
      />
      <PageHeader
        title="Grupe atributa"
        extra={
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddCircleOutlineOutlinedIcon />}
            onClick={() => setModalVisible(true)}
          >
            <Box fontSize="small">Nova grupa atributa</Box>
          </Button>
        }
      />
      <PageContentWrapper>
        <Breadcrumbs style={{ paddingBottom: `${theme.spacing(5)}px` }}>
          <Link variant="body1" color="inherit" to="/" component={RouterLink}>
            Početna
          </Link>
          <Typography variant="body1" color="textPrimary">
            Šifarnici
          </Typography>
          <Typography variant="body1" color="textPrimary">
            Grupe atributa
          </Typography>
        </Breadcrumbs>
        <Card>
          <CardHeader
            title={
              <QuickSearch
                name="code"
                placeholder="Brza pretraga po šifri"
                setFilters={setFilters}
                mainFilter={filters}
                setPage={setPage}
              />
            }
            action={
              <Grid container spacing={4}>
                <Grid item>
                  <Button
                    color="primary"
                    disabled={Object.keys(filters).filter((k) => filters[k])?.length === 0}
                    startIcon={<CloseIcon fontSize="small" />}
                    onClick={() => setFilters({})}
                  >
                    <Box fontSize="small">Ukloni sve filtere</Box>
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<FilterListIcon fontSize="small" />}
                    onClick={() => setFilterModalVisible(true)}
                  >
                    <Box fontSize="small">Filteri</Box>
                  </Button>
                </Grid>
                <Grid item>
                  <ExcelExportButton
                    totalCount={attributeGroups.totalCount}
                    fields={fields}
                    sorters={[...sorters, '"ordNumber" ASC']}
                    filters={filters}
                    variant="contained"
                    routeName="group-attributes"
                    fileName="grupe-atributa"
                  />
                </Grid>
              </Grid>
            }
          />
          <Divider />
          {Object.keys(filters).filter((k) => filters[k])?.length !== 0 && (
            <>
              <Box ml={2} p={2} display="flex" alignItems="center" flexWrap="wrap">
                {Object.keys(filters).map(
                  (chip) =>
                    filters[chip] !== undefined && (
                      <FilterChip
                        color="primary"
                        variant="outlined"
                        key={chip}
                        label={`${getLabelText(chip)}: ${getChipValue(chip, filters)}`}
                        onDelete={() => handleChipDelete(chip, setFilters, setPage)}
                      />
                    ),
                )}
              </Box>
            </>
          )}
          <Table
            loading={loading}
            error={error}
            attributeGroups={attributeGroups.nodes}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            totalCount={attributeGroups.totalCount}
            sorters={sorters}
            setSorters={setSorters}
            history={history}
            setSelectedAttributeGroup={setSelectedAttributeGroup}
            setModalVisible={setModalVisible}
            setDeleteModalVisible={setDeleteModalVisible}
          />
        </Card>
      </PageContentWrapper>
    </VerticalBoxContainer>
  );
};

export default AttributeGroups;
